import { Button, Col, Space, Table, message, Tag, Tooltip, Row, Modal, Form, Input } from "antd";
import { useEffect, useState } from "react";
import { CheckCircleOutlined, CloseCircleOutlined, PlusCircleTwoTone, CopyOutlined, FileExcelOutlined, MailOutlined } from "@ant-design/icons";
import { Excel } from "antd-table-saveas-excel";
import {
    getRegistrationCodes,
    generateRegistrationCode,
    approveRegistration,
    rejectRegistration
} from "../../../services/registrationCodeApi";
import { selectCurrentAdminId } from "../../../utils/helpers/selectorHelper";
import ContainerLayout from "../../common/containerLayout";
import moment from "moment";

const { Column } = Table;

interface RegistrationCode {
    id: number;
    code: string;
    isUsed: boolean;
    expiresAt: string;
    createdAt: string;
    createdBy: number;
    userId: number | null;
    email: string | null;
    user: {
        id: number;
        firstName: string;
        lastName: string;
        email: string;
        registrationStatus: string;
    } | null;
}

const RegistrationCodeList = () => {
    const [data, setData] = useState<RegistrationCode[]>([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [emails, setEmails] = useState<string[]>([]);
    const [inputValue, setInputValue] = useState('');
    const adminId = selectCurrentAdminId();

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await getRegistrationCodes(adminId);
            if (response.success) {
                setData(response.data);
            } else {
                message.error(response.message || "Error al obtener los códigos");
            }
        } catch (error: any) {
            message.error(error.response?.data?.message || "Error de conexión");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleGenerateCode = async () => {
        setLoading(true);
        try {
            const response = await generateRegistrationCode();
            if (response.success) {
                message.success(response.message || "Código generado exitosamente");
                fetchData();
            } else {
                message.error(response.message || "Error al generar el código");
            }
        } catch (error: any) {
            message.error(error.response?.data?.message || "Error al generar el código");
        } finally {
            setLoading(false);
        }
    };

    const handleModalOk = async () => {
        if (emails.length === 0 && !inputValue) {
            message.warning("Por favor ingrese al menos un correo");
            return;
        }

        let emailsToProcess = [...emails];
        if (inputValue) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (emailRegex.test(inputValue)) {
                if (emailsToProcess.indexOf(inputValue) === -1) {
                    emailsToProcess.push(inputValue);
                }
            } else {
                message.error("El correo actual no es válido");
                return;
            }
        }

        setLoading(true);
        try {
            const results = await Promise.allSettled(
                emailsToProcess.map(email => generateRegistrationCode({ email }))
            );

            const successful = results.filter(r => r.status === 'fulfilled' && r.value.success).length;
            const failed = results.length - successful;

            if (successful > 0) {
                message.success(`${successful} código(s) generado(s) exitosamente`);
                setIsModalVisible(false);
                setEmails([]);
                setInputValue('');
                form.resetFields();
                fetchData();
            }

            if (failed > 0) {
                message.error(`${failed} código(s) no pudieron ser generados`);
            }
        } catch (error: any) {
            message.error("Error al procesar los correos");
        } finally {
            setLoading(false);
        }
    };

    const handleInputConfirm = () => {
        if (inputValue && emails.indexOf(inputValue) === -1) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (emailRegex.test(inputValue)) {
                setEmails([...emails, inputValue]);
                setInputValue('');
            } else {
                message.error("Ingrese un correo válido");
            }
        } else if (inputValue && emails.indexOf(inputValue) !== -1) {
            setInputValue('');
        }
    };

    const handleCloseTag = (removedEmail: string) => {
        setEmails(emails.filter((email) => email !== removedEmail));
    };

    const handleApprove = async (id: number) => {
        try {
            const response = await approveRegistration(id);
            if (response.success) {
                message.success(response.message || "Registro aprobado");
                fetchData();
            } else {
                message.error(response.message || "Error al aprobar");
            }
        } catch (error: any) {
            message.error(error.response?.data?.message || "Error al aprobar");
        }
    };

    const handleReject = async (id: number) => {
        try {
            const response = await rejectRegistration(id);
            if (response.success) {
                message.success(response.message || "Registro rechazado");
                fetchData();
            } else {
                message.error(response.message || "Error al rechazar");
            }
        } catch (error: any) {
            message.error(error.response?.data?.message || "Error al rechazar");
        }
    };

    const handleExportExcel = () => {
        const excel = new Excel();
        excel
            .addSheet("Códigos de Registro")
            .addColumns([
                { title: "ID Usuario", dataIndex: "userId" },
                { title: "Email", dataIndex: "email" },
                { title: "Código", dataIndex: "code" },
                { title: "URL", dataIndex: "url" },
                { title: "Estado", dataIndex: "status" },
                { title: "Creado el", dataIndex: "createdAt" },
                { title: "Expira el", dataIndex: "expiresAt" },
            ])
            .addDataSource(
                data.map((item) => {
                    return {
                        userId: item.user?.id || "N/A",
                        email: item.email || item.user?.email || "N/A",
                        code: item.code,
                        url: `${window.location.origin}/register?code=${item.code}${item.email ? `&email=${encodeURIComponent(item.email)}` : ""}`,
                        status: getStatusLabel(item),
                        createdAt: moment(item.createdAt).format("DD/MM/YYYY HH:mm"),
                        expiresAt: moment(item.expiresAt).format("DD/MM/YYYY HH:mm"),
                    };
                }),
                {
                    str2num: true,
                }
            )
            .saveAs("Codigos_de_Registro.xlsx");
    };

    const handleCopyUrl = (record: RegistrationCode) => {
        let url = `${window.location.origin}/register?code=${record.code}`;
        if (record.email) {
            url += `&email=${encodeURIComponent(record.email)}`;
        }
        navigator.clipboard.writeText(url);
        message.success("URL copiada al portapapeles");
    };

    const getStatusLabel = (record: RegistrationCode) => {
        const isExpired = moment().isAfter(moment(record.expiresAt));
        if (isExpired && !record.isUsed) return "EXPIRADO";

        const status = record.user?.registrationStatus;
        switch (status) {
            case "PENDING": return "PENDIENTE";
            case "APPROVED": return "APROBADO";
            case "REJECTED": return "RECHAZADO";
            default: return status || "POR REGISTRAR";
        }
    };

    const getStatusTag = (record: RegistrationCode) => {
        const label = getStatusLabel(record);
        switch (label) {
            case "EXPIRADO": return <Tag color="red">EXPIRADO</Tag>;
            case "PENDIENTE": return <Tag color="gold">PENDIENTE</Tag>;
            case "APROBADO": return <Tag color="green">APROBADO</Tag>;
            case "RECHAZADO": return <Tag color="red">RECHAZADO</Tag>;
            default: return <Tag>{label}</Tag>;
        }
    };

    return (
        <ContainerLayout title="Códigos de Registro" showNotification={false} defaultContainer={false}>
            <Row gutter={[32, 16]} style={{ padding: "16px 0", margin: 0 }}>
                <Col span={24}>
                    <Space>
                        <Button
                            type="primary"
                            icon={<PlusCircleTwoTone />}
                            onClick={handleGenerateCode}
                            loading={loading}
                        >
                            Generar Código
                        </Button>
                        <Button
                            icon={<MailOutlined />}
                            onClick={() => setIsModalVisible(true)}
                            loading={loading}
                        >
                            Generar y Enviar Invitaciones
                        </Button>
                        <Button
                            icon={<FileExcelOutlined style={{ color: "#1d6f42" }} />}
                            onClick={handleExportExcel}
                            disabled={data.length === 0}
                        >
                            Exportar a Excel
                        </Button>
                    </Space>
                </Col>
                <Col span={24}>
                    <Table
                        dataSource={data}
                        rowKey="id"
                        loading={loading}
                        pagination={{ pageSize: 10 }}
                        scroll={{ x: 1000, y: "calc(100vh - 280px)" }}
                    >
                        <Column
                            title="ID del Usuario"
                            render={(record: RegistrationCode) => record.user?.id || "N/A"}
                            width={100}
                            sorter={(a: RegistrationCode, b: RegistrationCode) => (a.user?.id || 0) - (b.user?.id || 0)}
                        />
                        <Column
                            title="Email"
                            render={(record: RegistrationCode) => record.email || record.user?.email || "N/A"}
                            width={200}
                            sorter={(a: RegistrationCode, b: RegistrationCode) => {
                                const emailA = a.email || a.user?.email || "";
                                const emailB = b.email || b.user?.email || "";
                                return emailA.localeCompare(emailB);
                            }}
                        />
                        <Column
                            title="Código"
                            dataIndex="code"
                            width={180}
                            sorter={(a: RegistrationCode, b: RegistrationCode) => a.code.localeCompare(b.code)}
                        />
                        <Column
                            title="Estado Registro"
                            render={(record: RegistrationCode) => getStatusTag(record)}
                            width={150}
                            sorter={(a: RegistrationCode, b: RegistrationCode) => getStatusLabel(a).localeCompare(getStatusLabel(b))}
                        />
                        <Column
                            title="Creado el"
                            render={(record: RegistrationCode) => moment(record.createdAt).format("DD/MM/YYYY HH:mm")}
                            width={180}
                            sorter={(a: RegistrationCode, b: RegistrationCode) => moment(a.createdAt).unix() - moment(b.createdAt).unix()}
                        />
                        <Column
                            title="Expira el"
                            render={(record: RegistrationCode) => moment(record.expiresAt).format("DD/MM/YYYY HH:mm")}
                            width={180}
                            sorter={(a: RegistrationCode, b: RegistrationCode) => moment(a.expiresAt).unix() - moment(b.expiresAt).unix()}
                        />
                        <Column
                            title="Acciones"
                            fixed="right"
                            width={120}
                            render={(record: RegistrationCode) => {
                                const isPending = record.user?.registrationStatus === "PENDING";
                                return (
                                    <Space size="middle">
                                        <Tooltip title="Copiar URL">
                                            <Button
                                                icon={<CopyOutlined />}
                                                type="text"
                                                onClick={() => handleCopyUrl(record)}
                                            />
                                        </Tooltip>
                                        <Tooltip title="Aprobar">
                                            <Button
                                                icon={<CheckCircleOutlined style={{ color: isPending ? "#52c41a" : "#d9d9d9" }} />}
                                                type="text"
                                                disabled={!isPending}
                                                onClick={() => record.user && handleApprove(record.user.id)}
                                            />
                                        </Tooltip>
                                        <Tooltip title="Rechazar">
                                            <Button
                                                icon={<CloseCircleOutlined style={{ color: isPending ? "#f5222d" : "#d9d9d9" }} />}
                                                type="text"
                                                disabled={!isPending}
                                                onClick={() => record.user && handleReject(record.user.id)}
                                            />
                                        </Tooltip>
                                    </Space>
                                );
                            }}
                        />
                    </Table>
                </Col>
            </Row>
            <Modal
                title="Enviar link de registro a correo"
                open={isModalVisible}
                onOk={handleModalOk}
                onCancel={() => {
                    setIsModalVisible(false);
                    setEmails([]);
                    setInputValue('');
                    form.resetFields();
                }}
                confirmLoading={loading}
                okText="Enviar"
                cancelText="Cancelar"
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        label="Correos Electrónicos"
                        help="Escriba un correo y presione Enter para agregarlo"
                    >
                        <div style={{ marginBottom: emails.length > 0 ? 8 : 0 }}>
                            {emails.map((email) => (
                                <Tag
                                    key={email}
                                    closable
                                    onClose={() => handleCloseTag(email)}
                                    style={{ marginBottom: 8 }}
                                >
                                    {email}
                                </Tag>
                            ))}
                        </div>
                        <Input
                            placeholder="ejemplo@correo.com"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onPressEnter={(e) => {
                                e.preventDefault();
                                handleInputConfirm();
                            }}
                            onBlur={handleInputConfirm}
                            maxLength={255}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </ContainerLayout>
    );
};

export default RegistrationCodeList;
